/**
 * filterDuplicateSubscriptions
 *
 * Collapses a merged subscription list down to exactly ONE entry per `userId`.
 *
 * Why the sheet needs it — both of its lists concatenate rows that can name the same peer twice:
 *  - Search: `performSearch` fires the `firstLastNameLower` and the `lastFirstNameLower` query and
 *    concatenates both pages, so a user whose name matches on both sides comes back twice.
 *  - Subscriptions: `fetchSubscriptions` queries with no `meSubscriber` filter, so a mutual pair
 *    (each user subscribed to the other) yields two documents with the same `userId` and a
 *    different `subscriberUserId`.
 *
 * Flutter parity — ONE row per user id, first occurrence wins. Flutter merges its two search pages
 * with `_searchSubscriptions.addAllUnique(...)` (`search_users_bottom_sheet.dart`,
 * `onSearchSubscriptions`, :274 for the by-first-name page and :288 for the by-last-name page);
 * `addAllUnique` (`lib/domain/extensions/list_extensions.dart:5-11`) appends an element only when
 * the list does not already `contains` it, and `ExclusiveContentSubscription.operator ==` compares
 * `userId` ALONE (`lib/data/models/exclusive_content_subscription.dart:58-61`). So Flutter keeps
 * the first sighting of each `userId` and never renders a user twice — the behaviour reproduced
 * here.
 *
 * Two deliberate, pre-existing divergences ride along:
 *
 * 1. Flutter's subscriptions branch does NOT de-duplicate at all — `getSubscriptions` uses a plain
 *    `_subscriptions.addAll(response.subscriptions)` (`search_users_bottom_sheet.dart:234`), so a
 *    mutual pair renders as two identical-looking rows there. That is an obvious Flutter bug
 *    (`conventions.md` → "Handling obvious Flutter bugs"); web does not port it and routes both
 *    branches through this helper instead. Follow-up recommended to switch Flutter's `addAll` to
 *    the `addAllUnique` its own search branch already uses.
 *
 * 2. Tie-break: when duplicates disagree on `meSubscriber`, web keeps the `meSubscriber: false`
 *    copy instead of the first one. This only decides the rendered
 *    "Subscription"/"Subscriber" label (`SearchUsersUserItem`), never row identity or share
 *    targeting: every document under `users/{me}/subscriptions` stores the PEER in `userId`, and
 *    for the `meSubscriber: false` direction `subscriberUserId` is that same peer
 *    (`cloud_functions/functions/src/functions/user_subscriptions/user_subscriptions_utils.ts:224-248`),
 *    so `ShareWithFriendsUseCase`'s `meSubscriber ? userId : subscriberUserId` resolves to the same
 *    peer whichever copy survives.
 *
 * The previous implementation implemented that tie-break as "keep every copy whose `meSubscriber`
 * is false", which kept ALL of them when no copy had `meSubscriber: true` — the duplicate row +
 * React duplicate-key `console.error` found by browser QA (`VirtualList` keys rows on
 * `subscription.userId`). Selection is index-free (`toggleSubscriptionSelection` matches on
 * `userId`), so the survivor's position is the only other observable, and it is unchanged: the kept
 * entry stays at its own original index.
 */

import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';

export function filterDuplicateSubscriptions(
  subscriptions: ExclusiveContentSubscriptionEntity[],
): ExclusiveContentSubscriptionEntity[] {
  const keptByUserId = new Map<
    string,
    { index: number; subscription: ExclusiveContentSubscriptionEntity }
  >();

  subscriptions.forEach((subscription, index) => {
    const kept = keptByUserId.get(subscription.userId);

    if (kept === undefined) {
      // First sighting of this user id always wins — Flutter's `addAllUnique`.
      keptByUserId.set(subscription.userId, { index, subscription });
      return;
    }

    // The ONLY displacement allowed: a `meSubscriber: false` copy replaces a kept
    // `meSubscriber: true` one. Because a kept `false` copy is never displaced, each user id
    // resolves to exactly one entry no matter how the duplicates are ordered or how many share
    // the same `meSubscriber` value.
    if (kept.subscription.meSubscriber && !subscription.meSubscriber) {
      keptByUserId.set(subscription.userId, { index, subscription });
    }
  });

  const keptIndexes = new Set<number>();
  keptByUserId.forEach((kept) => keptIndexes.add(kept.index));

  return subscriptions.filter((_, index) => keptIndexes.has(index));
}
